import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdAdd, MdDelete, MdEdit, MdCheck, MdClose, MdSearch } from 'react-icons/md';

function ManageSection({ title, endpoint, fields }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = items.filter(item => {
    const q = searchQuery.toLowerCase();
    return (
      (item.name || '').toLowerCase().includes(q) ||
      (item.slug || '').toLowerCase().includes(q) ||
      (item.state || '').toLowerCase().includes(q)
    );
  });

  const fetchItems = async () => {
    try {
      const res = await api.get(endpoint);
      setItems(res.data.data);
    } catch (err) {
      toast.error(`Failed to load ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleAiGenerate = async (isEdit = false) => {
    const sourceName = isEdit ? editForm.name : form.name;
    if (!sourceName) {
      return toast.error('Please enter a name first');
    }

    setAiLoading(true);
    try {
      const res = await api.post('/admin/ai/generate', { type: 'slug', title: sourceName });
      if (res.data.success) {
        if (isEdit) {
          setEditForm(prev => ({ ...prev, slug: res.data.data }));
        } else {
          setForm(prev => ({ ...prev, slug: res.data.data }));
        }
        toast.success('Slug generated');
      }
    } catch (err) {
      toast.error('AI generation failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post(endpoint, form);
      toast.success(`${title.slice(0, -1)} added`);
      setForm({});
      setShowForm(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`${endpoint}/${deleteModal.id}`);
      toast.success('Deleted');
      setDeleteModal(null);
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    const ef = {};
    fields.forEach(f => { ef[f.key] = item[f.key] || ''; });
    setEditForm(ef);
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`${endpoint}/${id}`, editForm);
      toast.success('Updated');
      setEditingId(null);
      fetchItems();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  return (
    <div className="manage-section">
      <div className="section-header">
        <h3>{title}</h3>
        <div className="flex-row items-center gap-2">
          <div className="search-input-wrap" style={{ maxWidth: '200px' }}>
            <MdSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <button className="btn btn-sm btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Add</>}
          </button>
        </div>
      </div>

      {showForm && (
        <form className="manage-form" onSubmit={handleCreate}>
          {fields.map(f => (
            f.type === 'checkbox' ? (
              <label key={f.key} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form[f.key] || false}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.checked }))}
                />
                {f.placeholder}
              </label>
            ) : (
              <div key={f.key} className="form-group mb-2">
                {f.key === 'slug' && (
                  <div className="flex-row justify-between items-center mb-1">
                    <label className="mb-0 text-xs font-bold uppercase text-muted">Slug</label>
                    <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleAiGenerate(false)} disabled={aiLoading}>
                      ✨ AI
                    </button>
                  </div>
                )}
                <input
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={form[f.key] || ''}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  required={f.required}
                />
              </div>
            )
          ))}
          <button type="submit" className="btn btn-sm btn-primary" disabled={aiLoading}>Add</button>
        </form>
      )}

      {loading ? <p className="text-muted">Loading...</p> : (
        <div className="manage-list">
          {filteredItems.map(item => (
            <div key={item.id} className="manage-item">
              {editingId === item.id ? (
                <div className="manage-edit-row">
                  {fields.map(f => (
                    f.type === 'checkbox' ? (
                      <label key={f.key} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={editForm[f.key] || false}
                          onChange={e => setEditForm(prev => ({ ...prev, [f.key]: e.target.checked }))}
                        />
                        {f.placeholder}
                      </label>
                    ) : (
                      <div key={f.key} className="form-group mb-2">
                        {f.key === 'slug' && (
                          <div className="flex-row justify-between items-center mb-1">
                            <label className="mb-0 text-xs font-bold uppercase text-muted">Slug</label>
                            <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleAiGenerate(true)} disabled={aiLoading}>
                              ✨ AI
                            </button>
                          </div>
                        )}
                        <input
                          type={f.type || 'text'}
                          value={editForm[f.key] || ''}
                          onChange={e => setEditForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        />
                      </div>
                    )
                  ))}
                  <div className="flex-row gap-2 mt-2">
                    <button className="btn btn-sm btn-primary" onClick={() => saveEdit(item.id)} disabled={aiLoading}><MdCheck /></button>
                    <button className="btn btn-sm btn-ghost" onClick={() => setEditingId(null)}><MdClose /></button>
                  </div>
                </div>
              ) : (
                <div className="manage-display-row">
                  <div className="manage-item-info">
                    <span className="manage-name">{item.name}</span>
                    {item.state && <span className="manage-sub">{item.state}</span>}
                    {item.description && <span className="manage-sub">{item.description}</span>}
                    <span className="manage-slug">{item.slug}</span>
                    {fields.filter(f => f.type === 'checkbox').map(f => (
                      item[f.key] && <span key={f.key} className="badge badge-success ml-2">{f.label || f.placeholder}</span>
                    ))}
                  </div>
                  <div className="action-btns">
                    <button className="btn btn-sm btn-ghost" onClick={() => startEdit(item)}><MdEdit /></button>
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(item)}><MdDelete /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {filteredItems.length === 0 && <p className="text-muted">No {title.toLowerCase()} found</p>}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (title === 'Categories' ? createPortal(
        <div 
          className="modal-overlay" 
          onClick={() => setDeleteModal(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '20px',
            boxSizing: 'border-box'
          }}
        >
          <div 
            className="modal modal-sm" 
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '440px',
              width: '100%',
              backgroundColor: 'var(--card)',
              borderRadius: '16px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              overflow: 'hidden',
              margin: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div 
              className="modal-header"
              style={{
                padding: '18px 24px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'var(--text)' }}>
                Delete Category
              </h3>
              <button 
                className="modal-close" 
                onClick={() => setDeleteModal(null)}
                style={{
                  background: 'var(--bg)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-secondary)'
                }}
              >
                <MdClose size={20} />
              </button>
            </div>
            <div 
              className="modal-body"
              style={{
                padding: '20px 24px'
              }}
            >
              <p style={{ margin: 0, fontSize: '15px', color: 'var(--text)', lineHeight: '1.5' }}>
                Are you sure you want to delete the category <strong>"{deleteModal.name}"</strong>?
              </p>
              <p className="text-muted" style={{ margin: '8px 0 0 0', fontSize: '13px' }}>This action cannot be undone.</p>
            </div>
            <div 
              className="modal-footer"
              style={{
                padding: '16px 24px',
                borderTop: '1px solid #E5E7EB',
                backgroundColor: 'var(--bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '12px'
              }}
            >
              <button 
                className="btn btn-ghost" 
                onClick={() => setDeleteModal(null)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  height: '38px',
                  margin: 0
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger-solid" 
                onClick={confirmDelete} 
                disabled={deleting}
                style={{
                  padding: '8px 18px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  backgroundColor: '#EF233C',
                  color: '#ffffff',
                  border: 'none',
                  height: '38px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: 0
                }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete City</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this city?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.name}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Manage() {
  return (
    <div className="page">
      <div className="page-header">
        <h2>Manage Categories & Cities</h2>
      </div>

      <div className="manage-grid">
        <div className="card">
          <ManageSection
            title="Categories"
            endpoint="/categories"
            fields={[
              { key: 'name', placeholder: 'Category name (e.g. Markets)', required: true },
              { key: 'slug', placeholder: 'Slug (English only, e.g. markets)', required: true },
              { key: 'description', placeholder: 'Description (optional)', required: false },
              { key: 'show_on_home', placeholder: 'Show on home page', type: 'checkbox', label: 'On Home' },
            ]}
          />
        </div>

        <div className="card">
          <ManageSection
            title="Cities"
            endpoint="/cities"
            fields={[
              { key: 'name', placeholder: 'City name (e.g. Mumbai)', required: true },
              { key: 'slug', placeholder: 'Slug (English only, e.g. mumbai)', required: true },
              { key: 'state', placeholder: 'State (e.g. Maharashtra)', required: false },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
